import type { SubmitHandler } from 'react-hook-form';
import { faSignalStream } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiUpdateLiveStreamResponseBody } from '~/lib/api/modules/LiveStreams';
import type { IUseUpdateLiveStreamMutationValues } from '~/lib/api/modules/LiveStreams/hooks/useUpdateLiveStreamMutation';
import type { ILiveStreamFormValues } from './LiveStreamForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useLiveStreamQuery } from '~/lib/api/modules/LiveStreams/hooks/useLiveStreamQuery';
import { useUpdateLiveStreamMutation } from '~/lib/api/modules/LiveStreams/hooks/useUpdateLiveStreamMutation';
import { LiveStreamForm } from './LiveStreamForm';

export type IUpdateLiveStreamFormProps = IContainerProps & {
  onSuccess?: (
    outputData: IApiUpdateLiveStreamResponseBody,
    inputValues: IUseUpdateLiveStreamMutationValues,
  ) => void;
  cancelUrl?: string;
  appId: string;
  liveStreamId: string;
};

export const UpdateLiveStreamForm: React.FC<IUpdateLiveStreamFormProps> = (
  props,
) => {
  const { sx, onSuccess, cancelUrl, appId, liveStreamId } = props;
  const liveStreamQuery = useLiveStreamQuery({ appId, liveStreamId });
  const updateLiveStreamMutation = useUpdateLiveStreamMutation();
  const { t } = useTranslation();

  const liveStream = liveStreamQuery.data;

  const handleSubmit: SubmitHandler<ILiveStreamFormValues> = (values) => {
    if (updateLiveStreamMutation.isLoading) {
      return;
    }

    return updateLiveStreamMutation.mutateAsync(
      {
        appId,
        liveStreamId,
        body: {
          name: values.name!,
          startDateTime: values.startDateTime!,
        },
      },
      { onSuccess },
    );
  };

  return (
    <ContentBlock sx={sx} draftOutlineSize="lg">
      <ContentBlock.Title icon={<FontAwesomeIcon icon={faSignalStream} />}>
        {t('UpdateLiveStreamForm.title')}
      </ContentBlock.Title>

      <ContentBlock.Suspense
        loading={liveStreamQuery.isLoading}
        error={liveStreamQuery.error}
      >
        {liveStream && (
          <LiveStreamForm
            mode="edition"
            defaultValues={{
              name: liveStream.displayName ?? '',
              startDateTime: liveStream.startDateTime ?? '',
            }}
            cancelUrl={cancelUrl}
            onSubmit={handleSubmit}
          />
        )}
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
